import i18n, {
  type BackendModule,
  type InitOptions,
  type ReadCallback,
  type ResourceKey,
} from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { ACCOUNT_LANGUAGES, bindDocumentLanguage } from './lib/uiLanguage';
import { bundledResources } from './i18nBundledResources';

/** Nalagalnik enega jezikovnega svežnja (dinamični `import()` → lasten kos). */
export type LocaleLoader = () => Promise<{ default: unknown }>;

/**
 * T2885 — prevodi se naložijo LENOBNO, po jeziku (odločitev Alena 22.10.2026).
 *
 * Do T2885 je bilo vseh pet `translation.json` (~1,45 MB, ~435 kB gzip) v
 * vstopnem grafu (T2883 jih je dal v en kos `locales`), uporabnik pa rabi en
 * jezik in rezervo `en` (`fallbackLng`). Vsak jezik je zdaj svoj kos, naloži ga
 * zaledni vtičnik spodaj:
 *  - ob zagonu samo zaznani/shranjeni jezik (rezerva `en` od T2899 le ob
 *    zgrešenem ključu, gl. `i18nOptionsFor`),
 *  - ostale ob `changeLanguage` ali prej, s predhodnim nalaganjem
 *    (`preloadLanguage` — preklopnik ga sproži ob hoverju/fokusu).
 *
 * `changeLanguage` jezika ne zamenja, dokler njegov sveženj ni naložen
 * (i18next pokliče `loadResources` pred `languageChanged`), zato vmesnik med
 * nalaganjem kaže STARA besedila, nikoli golih ključev, in se zamenja naenkrat.
 */
const LOCALE_LOADERS: Record<string, LocaleLoader> = {
  sl: () => import('./locales/sl/translation.json'),
  en: () => import('./locales/en/translation.json'),
  de: () => import('./locales/de/translation.json'),
  hr: () => import('./locales/hr/translation.json'),
  it: () => import('./locales/it/translation.json'),
};

/**
 * Zaledni vtičnik i18next nad nalagalniki. Izvožen kot tovarna, da test
 * (T2885) meri vedenje z lastnimi, zadržanimi nalagalniki.
 */
export function createLazyLocaleBackend(loaders: Record<string, LocaleLoader>): BackendModule {
  return {
    type: 'backend',
    init() {},
    read(language: string, _namespace: string, callback: ReadCallback) {
      const load = loaders[language] ?? loaders[language.split('-')[0]];
      if (!load) {
        // Jezik brez prevodov (npr. `dev`): prazen sveženj, rezerva prevzame.
        callback(null, {});
        return;
      }
      load().then(
        (mod) => callback(null, mod.default as ResourceKey),
        (err: unknown) => callback(err instanceof Error ? err : new Error(String(err)), false),
      );
    },
  };
}

/** Rezervni jezik za ključ, ki ga v izbranem jeziku ni (T2103). */
export const FALLBACK_LANGUAGE = 'en';

type I18nInstance = typeof i18n;

/**
 * Nastavitve brez virov za podani primerek — izvožene, da test (T2103, T2885,
 * T2899) požene iste nastavitve na svežem primerku.
 *
 * T2103 (odločitev Alena 27.9.2026): tuj uporabnik z nepodprtim jezikom
 * brskalnika (npr. `fr-FR`) dobi **angleščino**, ne slovenščine. `supportedLngs`
 * omeji zaznani jezik na pet jezikov računa (`sl-SI` → `sl`, `fr-FR` → rezerva),
 * rezerva pa je `en` — tako ob prvem obisku kot za ključ, ki ga v tujem
 * jeziku (še) ni. Slovenski brskalnik ostane slovenski.
 *
 * T2899 (odločitev Alena 22.10.2026) — rezerva `en` se NE naloži ob zagonu, ko
 * uporabnik ni angleški (−80 kB gzip začetnega prenosa). Rezerva je funkcija:
 *  - brez jezika (izbira jezika ob prvem obisku) vrne `en` (T2103),
 *  - za angleščino in kadar je sveženj `en` že v pomnilniku vrne `en`,
 *  - sicer nič — i18next ob zagonu naloži samo izbrani jezik.
 * Če ključa v izbranem jeziku ni, `missingKeyHandler` (`saveMissing`) sveženj
 * `en` naloži v ozadju; i18next ob tem sproži `loaded`, ki ga react-i18next
 * posluša (`bindI18n`), in vmesnik se ponovno izriše z angleškim besedilom.
 * Do tedaj se izpiše `defaultValue` ključa (ali ključ, če ga nima).
 *
 * Zakaj na zahtevo in ne vedno v ozadju po prvem izrisu: vsi štirje ne-angleški
 * svežnji vsebujejo vse ključe iz `en` (varuje `locales/__tests__/locale-parity`),
 * zato rezerve v praksi nikoli ne potrebujemo — nalaganje v ozadju bi 80 kB
 * prenašalo vsakemu uporabniku zaman. Na zahtevo je le varovalo za prihodnjo
 * vrzel med jeziki.
 */
export function i18nOptionsFor(inst: I18nInstance): InitOptions {
  let fallbackRequested = false;
  const fallbackLoaded = () => Boolean(inst.store) && inst.hasResourceBundle(FALLBACK_LANGUAGE, 'translation');
  return {
    fallbackLng: (code?: string) =>
      !code || code.split('-')[0] === FALLBACK_LANGUAGE || fallbackLoaded() ? [FALLBACK_LANGUAGE] : [],
    supportedLngs: [...ACCOUNT_LANGUAGES],
    debug: import.meta.env.DEV, // T2031: prej `true` tudi v produkciji
    interpolation: {
      escapeValue: false, // React že ščiti pred XSS
    },
    // T3038: velja za neprijavljene; ob prijavi oz. veljavni seji jezik
    // prevzame `user.language` (`authStore.ts` `applyAccountLanguage`).
    detection: {
      order: ['localStorage', 'cookie', 'navigator'],
      caches: ['localStorage', 'cookie'],
    },
    // T2899: zgrešen ključ naloži rezervo `en` (gl. zgoraj). Zaledni vtičnik nima
    // `create`, zato `saveMissing` nikamor ne piše — rabimo le povratni klic.
    saveMissing: true,
    missingKeyHandler: (_lngs, _ns, _key, _fallbackValue, _updateMissing, options) => {
      // Ključ z `defaultValue` nikoli ne pokaže golega ključa, `en` pa ga tudi
      // nima (sl ⊇ en, ostali = en) — zanj rezerve ne nalagamo.
      if ((options as { defaultValue?: unknown } | undefined)?.defaultValue !== undefined) return;
      if (fallbackRequested || fallbackLoaded()) return;
      fallbackRequested = true;
      inst.loadLanguages(FALLBACK_LANGUAGE).then(
        () => undefined,
        () => {
          fallbackRequested = false; // naslednji zgrešen ključ poskusi znova
        },
      );
    },
    react: {
      // `loaded` = naložen sveženj (rezerva `en` ali predhodno naložen jezik):
      // komponente se ponovno izrišejo in zgrešen ključ dobi angleško besedilo.
      bindI18n: 'languageChanged loaded',
    },
  };
}

/** Nastavitve aplikacijskega primerka (`i18n`). */
export const i18nOptions: InitOptions = i18nOptionsFor(i18n);

i18n.use(LanguageDetector).use(initReactI18next);
// Testno okolje (vitest) dobi vseh pet svežnjev SINHRONO prek aliasa
// `i18nBundledResources` (vite.config.ts, `test.alias`), da testi z realnim
// i18n ostanejo deterministični. V gradnji je `bundledResources` `undefined`.
if (!bundledResources) i18n.use(createLazyLocaleBackend(LOCALE_LOADERS));

/**
 * Razreši se, ko je naložen jezik ob zagonu. `main.tsx` izriše
 * aplikacijo šele po tem, da prvi izris ne pokaže golih ključev. Razreši se
 * tudi ob napaki nalaganja (i18next pokliče povratni klic z napako), da
 * aplikacija vseeno vstane, namesto da bi ostala prazna.
 */
export const i18nReady: Promise<unknown> = i18n.init(
  bundledResources ? { ...i18nOptions, resources: bundledResources } : i18nOptions,
);

/**
 * T2885 — predhodno naloži jezik (npr. ob hoverju preklopnika), da je ob kliku
 * že v predpomnilniku in se preklop zgodi brez čakanja. Napaka je tiha:
 * `changeLanguage` nalaganje poskusi znova sam.
 */
export function preloadLanguage(lng: string): Promise<void> {
  return i18n.loadLanguages(lng).then(
    () => undefined,
    () => undefined,
  );
}

// T2046: `<html lang>` sledi jeziku vmesnika (bralniki zaslona izberejo glas po njem).
bindDocumentLanguage(i18n);

export default i18n;
